import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import {
  useChangePlanMutation,
  useGetPlanChangeOptionsQuery,
} from '../../features/users/userApiSlice';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatEuro = (value) => `€${Number(value || 0).toFixed(2)}`;

const cadenceMonthsFromCurrent = (current) => {
  const explicitCadence = Number(current?.cadenceMonths);
  if (Number.isFinite(explicitCadence) && explicitCadence > 0) {
    return explicitCadence === 3 ? 3 : 1;
  }
  const productType = String(current?.product_type || '').toLowerCase();
  const durationNum = parseInt(String(current?.duration ?? ''), 10);
  if (productType === 'omega-3') {
    return durationNum === 3 ? 3 : 1;
  }
  return durationNum === 6 ? 3 : 1;
};

const cadenceMonthsFromOption = (option) => {
  if (option?.cadenceMonths != null && !Number.isNaN(Number(option.cadenceMonths))) {
    return Number(option.cadenceMonths);
  }
  if (option?.duration === 6) return 3;
  return 1;
};

const planTitle = (cadenceMonths) => (cadenceMonths === 3 ? 'Every 3 months' : 'Monthly');

const ComparisonBullet = ({ children }) => (
  <li className="t-flex t-gap-2 t-text-sm t-text-slate-700 t-mb-1.5 last:t-mb-0 t-leading-snug">
    <span className="t-shrink-0 t-text-teal-600 t-font-bold" aria-hidden>
      ✓
    </span>
    <span>{children}</span>
  </li>
);

const AdminChangePlanModal = ({
  open,
  onClose,
  userId,
  userSubscriptionId,
  userEmail,
  onSuccess,
}) => {
  const [stage, setStage] = useState('options');
  const [selectedOption, setSelectedOption] = useState(null);

  const { data, isFetching, isError, error, refetch } = useGetPlanChangeOptionsQuery(
    { userId, userSubscriptionId },
    { skip: !open || !userId || !userSubscriptionId },
  );

  const [changePlan, { isLoading: isChanging }] = useChangePlanMutation();

  useEffect(() => {
    if (open) {
      setStage('options');
      setSelectedOption(null);
    }
  }, [open, userSubscriptionId]);

  useEffect(() => {
    if (open && userId && userSubscriptionId) {
      refetch();
    }
  }, [open, userId, userSubscriptionId, refetch]);

  const current = data?.current;
  const options = data?.options || [];
  const currentCadenceMonths = cadenceMonthsFromCurrent(current);
  const selectedCadenceMonths = selectedOption ? cadenceMonthsFromOption(selectedOption) : 1;

  const optionTotalAmount = (option) => {
    const base = Number(option?.amount || 0);
    const addon = Number(option?.addon?.newAmount || 0);
    return base + (option?.addon?.newAmount != null ? addon : 0);
  };

  const optionCurrentTotal = (option) => {
    const base = Number(current?.amount || 0);
    const addon = Number(option?.addon?.currentAmount || 0);
    return base + (option?.addon?.currentAmount != null ? addon : 0);
  };

  const savingsPercentForOption = (option) => {
    if (!current || !option) return null;
    const optionCadence = cadenceMonthsFromOption(option);
    const currentTotal = optionCurrentTotal(option);
    const nextTotal = optionTotalAmount(option);
    const currentPerMonth =
      currentCadenceMonths > 0 ? currentTotal / currentCadenceMonths : currentTotal;
    const nextPerMonth = optionCadence > 0 ? nextTotal / optionCadence : nextTotal;
    if (!Number.isFinite(currentPerMonth) || currentPerMonth <= 0 || !Number.isFinite(nextPerMonth)) {
      return null;
    }
    const savingsPct = ((currentPerMonth - nextPerMonth) / currentPerMonth) * 100;
    if (!Number.isFinite(savingsPct) || savingsPct <= 0) return null;
    return Math.round(savingsPct);
  };

  const optionPriceShort = (option) => {
    const cadence = cadenceMonthsFromOption(option);
    const total = optionTotalAmount(option);
    const perMonth = cadence > 0 ? Number((total / cadence).toFixed(2)) : total;
    if (cadence === 3) {
      return `${formatEuro(total)} / 3 mo (${formatEuro(perMonth)}/mo)`;
    }
    return `${formatEuro(total)} / month`;
  };

  const effectiveCopy = (option) => {
    if (!current || !option?.effectiveDate) {
      return 'Takes effect at the next renewal. No charge today.';
    }
    return `Current plan runs until ${formatDate(option.effectiveDate)}. Then ${optionPriceShort(option)}.`;
  };

  const currentComparisonPoints = useMemo(() => {
    if (!current) return [];
    const points = [
      `Billed ${formatEuro(current.amount)} every ${currentCadenceMonths} month${currentCadenceMonths === 1 ? '' : 's'}`,
      `${formatEuro(Number(current.amount || 0) / currentCadenceMonths)} per month equivalent`,
    ];
    if (current?.product_type === 'riise_formula' && selectedOption?.addon?.newAmount != null) {
      points.push(
        `Omega-3 add-on: ${formatEuro(selectedOption.addon.currentAmount)} / ${currentCadenceMonths} mo`,
      );
    }
    return points;
  }, [current, currentCadenceMonths, selectedOption]);

  const nextComparisonPoints = useMemo(() => {
    if (!selectedOption) return [];
    const points = [
      `Billed ${formatEuro(selectedOption.amount)} every ${selectedCadenceMonths} month${selectedCadenceMonths === 1 ? '' : 's'}`,
      `${formatEuro(Number(selectedOption.amount || 0) / selectedCadenceMonths)} per month equivalent`,
    ];
    if (selectedOption?.addon?.newAmount != null) {
      points.push(
        `Omega-3 add-on: ${formatEuro(selectedOption.addon.newAmount)} / ${selectedCadenceMonths} mo`,
      );
    }
    return points;
  }, [selectedOption, selectedCadenceMonths]);

  const currentHeadlineAmount = useMemo(() => {
    if (!current || !selectedOption) return Number(current?.amount || 0);
    return optionCurrentTotal(selectedOption);
  }, [current, selectedOption]);

  const selectedHeadlineAmount = useMemo(() => {
    if (!selectedOption) return 0;
    return optionTotalAmount(selectedOption);
  }, [selectedOption]);

  const savingsBadgeLabel = useMemo(() => {
    if (!selectedOption) return null;
    const pct = savingsPercentForOption(selectedOption);
    return pct != null ? `Save ${pct}%` : null;
  }, [selectedOption]);

  const handleConfirm = async () => {
    if (!selectedOption || !userId || !userSubscriptionId) return;
    try {
      await changePlan({
        userId,
        userSubscriptionId,
        targetSubId: selectedOption.targetSubId,
      }).unwrap();
      toast.success('Plan change scheduled.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.data?.message || err?.data?.error || 'Could not change plan.');
    }
  };

  const toggleClose = () => {
    if (isChanging) return;
    onClose?.();
  };

  const productLabel =
    String(current?.product_type || '').toLowerCase() === 'omega-3' ? 'Omega-3' : 'Riise';

  const saveBadgeClass =
    't-absolute t-top-3 t-right-3 t-text-[0.7rem] t-font-bold t-px-2 t-py-0.5 t-rounded-full t-bg-emerald-600 t-text-white';

  const optionCardClass =
    't-relative t-block t-w-full t-text-left t-border t-border-slate-200 t-rounded-lg t-px-5 t-py-4 t-bg-white t-text-slate-900 t-cursor-pointer t-shadow-none hover:t-border-teal-500 hover:t-shadow-md focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-teal-500 focus-visible:t-outline-offset-2';

  return (
    <Modal isOpen={open} toggle={toggleClose} size="lg">
      <ModalHeader toggle={toggleClose}>
        {stage === 'options' ? 'Choose new plan' : 'Confirm plan change'}
      </ModalHeader>
      <ModalBody>
        {userEmail && (
          <p className="t-text-sm t-text-slate-500 t-mb-4">
            Customer: <strong className="t-text-slate-900 t-font-semibold">{userEmail}</strong> ·{' '}
            {productLabel} subscription #{userSubscriptionId}
          </p>
        )}

        {stage === 'options' && (
          <>
            <p className="t-text-sm t-text-slate-500 t-mb-4">
              Select the new billing cadence. No charge today — the change applies at the next renewal.
            </p>
            {isFetching && <p className="t-text-sm t-text-slate-500 t-mb-0">Loading options…</p>}
            {!isFetching && isError && (
              <p className="t-text-sm t-text-red-600 t-mb-0">
                {error?.data?.message || error?.data?.error || 'Could not load plan-change options.'}
              </p>
            )}
            {!isFetching && !isError && options.length === 0 && (
              <p className="t-text-sm t-text-slate-500 t-mb-0">
                No plan-change options available for this subscription.
              </p>
            )}
            {!isFetching && options.length > 0 && (
              <div className="t-flex t-flex-col t-gap-3">
                {options.map((option) => {
                  const savings = savingsPercentForOption(option);
                  const cadence = cadenceMonthsFromOption(option);
                  return (
                    <button
                      key={option.targetSubId}
                      type="button"
                      className={optionCardClass}
                      onClick={() => {
                        setSelectedOption(option);
                        setStage('confirm');
                      }}
                    >
                      {savings != null && (
                        <span className={saveBadgeClass}>Save {savings}%</span>
                      )}
                      <div className="t-flex t-justify-between t-items-start t-gap-4 t-flex-wrap t-pr-16">
                        <h6 className="t-text-base t-font-semibold t-text-slate-900 t-m-0">
                          {planTitle(cadence)}
                        </h6>
                        <div className="t-flex t-flex-col t-items-end t-gap-0.5">
                          <span className="t-text-xs t-text-slate-400 t-line-through">
                            {formatEuro(optionCurrentTotal(option))}
                          </span>
                          <span className="t-text-sm t-font-semibold t-text-teal-700 t-text-right">
                            {optionPriceShort(option)}
                          </span>
                        </div>
                      </div>
                      <p className="t-text-sm t-text-slate-600 t-mt-2.5 t-mb-0 t-leading-snug">
                        {effectiveCopy(option)}
                      </p>
                      {option.addon?.newAmount != null && (
                        <p className="t-text-sm t-text-slate-700 t-mt-1.5 t-mb-0">
                          Omega-3 add-on updates to {formatEuro(option.addon.newAmount)} every{' '}
                          {option.addon.cadenceMonths ?? cadence} months (same renewal as Riise).
                        </p>
                      )}
                      <p className="t-text-xs t-font-semibold t-text-emerald-600 t-mt-2 t-mb-0">
                        No charge today
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {stage === 'confirm' && selectedOption && current && (
          <>
            <div className="t-grid t-grid-cols-1 sm:t-grid-cols-2 t-gap-4 t-mb-4">
              <div className="t-border t-border-slate-200 t-rounded-lg t-p-4 t-bg-slate-50 t-h-full">
                <p className="t-text-[0.7rem] t-font-bold t-uppercase t-tracking-wide t-text-slate-500 t-m-0 t-mb-2">
                  Current
                </p>
                <p className="t-text-base t-font-semibold t-text-slate-900 t-m-0 t-mb-1">
                  {planTitle(currentCadenceMonths)}
                </p>
                <p className="t-text-xl t-font-bold t-text-slate-900 t-m-0 t-mb-3">
                  {formatEuro(currentHeadlineAmount)}
                </p>
                <ul className="t-list-none t-m-0 t-p-0">
                  {currentComparisonPoints.map((point) => (
                    <ComparisonBullet key={`cur-${point}`}>{point}</ComparisonBullet>
                  ))}
                </ul>
              </div>
              <div className="t-relative t-border t-border-teal-300 t-rounded-lg t-p-4 t-bg-teal-50 t-h-full">
                {savingsBadgeLabel && (
                  <span className={saveBadgeClass}>{savingsBadgeLabel}</span>
                )}
                <p className="t-text-[0.7rem] t-font-bold t-uppercase t-tracking-wide t-text-teal-700 t-m-0 t-mb-2 sm:t-pr-16">
                  New plan
                </p>
                <p className="t-text-base t-font-semibold t-text-slate-900 t-m-0 t-mb-1">
                  {planTitle(selectedCadenceMonths)}
                </p>
                <p className="t-text-xl t-font-bold t-text-slate-900 t-m-0 t-mb-3">
                  {formatEuro(selectedHeadlineAmount)}
                </p>
                <ul className="t-list-none t-m-0 t-p-0">
                  {nextComparisonPoints.map((point) => (
                    <ComparisonBullet key={`next-${point}`}>{point}</ComparisonBullet>
                  ))}
                </ul>
              </div>
            </div>
            <div className="t-bg-emerald-50 t-border t-border-emerald-200 t-rounded-lg t-px-4 t-py-3 t-text-sm t-text-emerald-800 t-leading-snug t-mb-4">
              {effectiveCopy(selectedOption)}
            </div>
            <div className="t-flex t-justify-end t-gap-2 t-mt-2">
              <Button color="secondary" outline onClick={() => setStage('options')} disabled={isChanging}>
                Back
              </Button>
              <Button color="primary" onClick={handleConfirm} disabled={isChanging}>
                {isChanging ? 'Updating…' : 'Confirm change'}
              </Button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AdminChangePlanModal;
